import { getToken, TENANT_ID_TOKEN_KEY } from "./authToken";
import { handleUnauthorizedResponse } from "./authSession";

const GRAPHQL_URL = import.meta.env.VITE_GRAPHQL_ENDPOINT || "/api/v2/sami/cms/graphql";
const GRAPHQL_TOKEN = import.meta.env.VITE_GRAPHQL_TOKEN || "";
const CMS_REST_BASE = GRAPHQL_URL.replace(/\/graphql\/?$/, "");

function hasStoredTenantToken(): boolean {
  return Boolean(localStorage.getItem(TENANT_ID_TOKEN_KEY));
}

function getAuthHeader(): string {
  const token = getToken();
  if (token) return `Bearer ${token}`;
  if (!hasStoredTenantToken() && GRAPHQL_TOKEN) return `Bearer ${GRAPHQL_TOKEN}`;
  return "";
}

export async function graphqlRequest<T>(
  query: string,
  variables?: Record<string, any>,
  skipAuthRetry = false,
): Promise<T> {
  const authHeader = getAuthHeader();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (authHeader) headers.Authorization = authHeader;

  const response = await fetch(GRAPHQL_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 401 && !skipAuthRetry) {
    const refreshed = await handleUnauthorizedResponse();
    if (refreshed) {
      return graphqlRequest<T>(query, variables, true);
    }
  }

  const bodyText = await response.text();
  if (!bodyText.trim()) {
    const hint =
      response.status === 502 || response.status === 504
        ? " The dev proxy could not reach the CMS API — start it with docker compose in api-app."
        : "";
    throw new Error(
      `Empty response from API (${response.status} ${response.statusText}).${hint}`,
    );
  }

  let json: { data?: T; errors?: Array<{ message?: string }> };
  try {
    json = JSON.parse(bodyText);
  } catch {
    throw new Error(
      `Invalid JSON from API (${response.status}): ${bodyText.slice(0, 200)}`,
    );
  }

  if (json.errors) {
    const err = json.errors[0];
    const message = err?.message ?? "GraphQL error";
    // Log full error for debugging (e.g. 500 on server - check for "Cannot return null for non-nullable field")
    console.error("GraphQL Error:", { message, fullErrors: json.errors });
    throw new Error(message);
  }

  if (json.data === undefined) {
    throw new Error("GraphQL response missing data");
  }

  return json.data;
}

export class CmsHttpError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "CmsHttpError";
  }
}

export async function cmsRestJson<T>(
  path: string,
  init: RequestInit = {},
  skipAuthRetry = false,
): Promise<T> {
  const authHeader = getAuthHeader();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (!headers["Content-Type"] && init.body && !(init.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }
  if (authHeader) headers.Authorization = authHeader;

  const response = await fetch(`${CMS_REST_BASE}${path}`, {
    ...init,
    headers,
  });

  if (response.status === 401 && !skipAuthRetry) {
    const refreshed = await handleUnauthorizedResponse();
    if (refreshed) {
      return cmsRestJson<T>(path, init, true);
    }
  }

  const bodyText = await response.text();
  if (!response.ok) {
    throw new CmsHttpError(
      response.status,
      bodyText.trim() || `Request failed (${response.status} ${response.statusText})`,
    );
  }
  if (!bodyText.trim()) {
    return {} as T;
  }
  return JSON.parse(bodyText) as T;
}

export async function convertFileToMarkdown(
  file: File,
  skipAuthRetry = false,
): Promise<{ markdown: string; title: string | null }> {
  const authHeader = getAuthHeader();
  const headers: Record<string, string> = {};
  if (authHeader) headers.Authorization = authHeader;

  const form = new FormData();
  form.append("file", file);

  const response = await fetch(`${CMS_REST_BASE}/markdown/convert`, {
    method: "POST",
    headers,
    body: form,
  });

  if (response.status === 401 && !skipAuthRetry) {
    const refreshed = await handleUnauthorizedResponse();
    if (refreshed) {
      return convertFileToMarkdown(file, true);
    }
  }

  const bodyText = await response.text();
  let json: {
    markdown?: string;
    title?: string | null;
    message?: string;
    error?: string;
    detail?: string | Array<{ msg?: string }>;
  };
  try {
    json = bodyText.trim() ? JSON.parse(bodyText) : {};
  } catch {
    throw new Error(
      `Invalid JSON from markdown convert (${response.status}): ${bodyText.slice(0, 200)}`,
    );
  }

  if (!response.ok) {
    const detail = json.detail;
    const detailText =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail) && typeof detail[0]?.msg === "string"
          ? detail[0].msg
          : "";
    const message =
      json.message ||
      json.error ||
      detailText ||
      `Markdown conversion failed (${response.status} ${response.statusText})`;
    throw new Error(message);
  }

  const markdown = (json.markdown ?? "").trim();
  if (!markdown) {
    throw new Error(json.error || json.message || "Markdown converter returned empty markdown");
  }

  return { markdown, title: json.title ?? null };
}
